import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { setUnauthorizedHandler } from "./sessionEvents";

const AuthContext = createContext(null);

const TOKEN_KEY = "pvapp_token";

const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return JSON.parse(window.atob(padded));
  } catch (_) {
    return null;
  }
};

const getTokenExpiryMs = (token) => {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp) || exp <= 0) return null;
  return exp * 1000;
};

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [authMessage, setAuthMessage] = useState("");
  const sessionExpiresAt = useMemo(() => getTokenExpiryMs(token), [token]);

  const clearSession = useCallback((message = "") => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setAuthMessage(message);
    setLoading(false);
  }, []);

  const handleUnauthorized = useCallback(
    (detail) => {
      const message =
        detail === "Token expired"
          ? "Your session expired. Please sign in again."
          : "Your session is no longer valid. Please sign in again.";
      clearSession(message);
    },
    [clearSession]
  );

  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    return () => setUnauthorizedHandler(null);
  }, [handleUnauthorized]);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    const fetchMe = async () => {
      try {
        const resp = await api.get("/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(resp.data);
      } catch (err) {
        console.error("Failed to load current user", err);
        handleUnauthorized(err?.response?.data?.detail);
      } finally {
        setLoading(false);
      }
    };

    fetchMe();
  }, [token, handleUnauthorized]);

  useEffect(() => {
    if (!token || !sessionExpiresAt) return undefined;
    const msRemaining = sessionExpiresAt - Date.now();
    if (msRemaining <= 0) {
      handleUnauthorized("Token expired");
      return undefined;
    }
    const timeoutId = window.setTimeout(() => handleUnauthorized("Token expired"), msRemaining + 250);
    return () => window.clearTimeout(timeoutId);
  }, [token, sessionExpiresAt, handleUnauthorized]);

  const login = useCallback(async (email, password) => {
    setAuthMessage("");
    const resp = await api.post("/auth/login", { email, password });
    const t = resp.data.access_token;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setUser(null);
  }, []);

  const refreshSession = useCallback(async () => {
    const resp = await api.post("/auth/refresh");
    const refreshedToken = resp.data.access_token;
    localStorage.setItem(TOKEN_KEY, refreshedToken);
    setAuthMessage("");
    setToken(refreshedToken);
    return resp.data;
  }, []);

  const logout = useCallback(() => {
    clearSession("");
  }, [clearSession]);

  const clearAuthMessage = useCallback(() => setAuthMessage(""), []);

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      login,
      logout,
      refreshSession,
      setUser,
      authMessage,
      clearAuthMessage,
      sessionExpiresAt,
    }),
    [token, user, loading, login, logout, refreshSession, authMessage, clearAuthMessage, sessionExpiresAt]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
